import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { FaTimes, FaStar } from "react-icons/fa";

import WebImage from "../assets/images/BerrBloomer_WebImage.webp";
import AndroidImage from "../assets/images/BerrBloomer_AndroidImage.jpg";
import IOSImage from "../assets/images/BerrBloomer_IOSImage.jpg";
import SaaSImage from "../assets/images/BerrBloomer_SaaSDashboard.jpg";
import ChatBotImage from "../assets/images/BerrBloomer_ChatBot.jpg";

/* ================= BRAND ================= */

const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";

/* ================= ANIMATIONS ================= */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(25px);}
  to { opacity: 1; transform: translateY(0);}
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95);}
  to { opacity: 1; transform: scale(1);}
`;

/* ================= WRAPPER ================= */

const Wrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  padding: 80px 20px;

  @media (max-width: 768px) {
    padding: 60px 16px;
  }

  @media (max-width: 480px) {
    padding: 40px 12px;
  }
`;

const Heading = styled.h1`
  text-align: center;
  font-size: 2.4rem;
  margin-bottom: 50px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

/* ================= GRID ================= */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const Tile = styled.div`
  background: #0f172a;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid #1e293b;
  animation: ${fadeUp} 0.6s ease forwards;
  transition: 0.3s;

  &:hover {
    transform: translateY(-6px);
    border-color: #0047ff;
  }

  @media (max-width: 480px) {
    padding: 18px;
  }
`;

const Media = styled.div`
  width: 100%;
  height: 170px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 15px;
  background: #0f172a;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 480px) {
    height: 150px;
  }
`;

const ProductName = styled.h3`
  color: #fff;
  margin-bottom: 8px;
`;

const Stars = styled.div`
  display: flex;
  align-items: center;
  color: #fbbf24;
  font-size: 0.85rem;
  margin-bottom: 10px;
`;

const TechStack = styled.div`
  font-size: 0.85rem;
  color: #cbd5e1;
  margin-bottom: 15px;
`;

const Price = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 15px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  background: ${BRAND_GRADIENT};
  color: #fff;
  font-weight: 600;
`;

const SecondaryButton = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 30px;
  border: 1px solid #0047ff;
  background: transparent;
  color: #0047ff;
  cursor: pointer;

  &:hover {
    background: ${BRAND_GRADIENT};
    color: #fff;
  }
`;

/* ================= MODAL ================= */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: ${({ show }) => (show ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  padding: 20px;
  z-index: 999;

  @media (max-width: 480px) {
    align-items: flex-start;
    padding-top: 60px;
    overflow-y: auto;
  }
`;

const Popup = styled.div`
  background: #020617;
  padding: 28px;
  border-radius: 18px;
  width: 100%;
  max-width: 500px;
  border: 1px solid #1e293b;
  animation: ${scaleIn} 0.25s ease forwards;
  position: relative;

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const Close = styled(FaTimes)`
  position: absolute;
  top: 18px;
  right: 18px;
  cursor: pointer;
  font-size: 1.2rem;
`;

const Description = styled.p`
  font-size: 0.9rem;
  color: #cbd5e1;
  margin: 15px 0;
`;

const TechList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TechTag = styled.span`
  background: #0f172a;
  color: #00c4ff;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
`;

/* ================= COMPONENT ================= */

const Buy = () => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "auto";
  }, [selected]);

  const products = [
    {
      id: 1,
      name: "AI Web Application",
      rating: 4,
      reviews: 310,
      description: "Scalable AI web app with authentication & OpenAI integration.",
      technologies: ["React.js", "Node.js", "MongoDB", "OpenAI API"],
      price: 1399,
      image: WebImage
    },
    {
      id: 2,
      name: "AI Android Application",
      rating: 5,
      reviews: 185,
      description: "AI-powered Android app with chatbot & Firebase backend.",
      technologies: ["React Native", "Firebase", "OpenAI API"],
      price: 1799,
      image: AndroidImage
    },
    {
      id: 3,
      name: "AI iOS Application",
      rating: 4,
      reviews: 142,
      description: "Premium AI-driven iOS application.",
      technologies: ["Swift", "Node.js", "MongoDB"],
      price: 1999,
      image: IOSImage
    },
    {
      id: 4,
      name: "AI SaaS Dashboard",
      rating: 5,
      reviews: 420,
      description: "Complete SaaS dashboard with analytics & role access.",
      technologies: ["React.js", "Chart.js", "Spring Boot"],
      price: 2499,
      image: SaaSImage
    },
    {
      id: 5,
      name: "AI Chatbot Integration",
      rating: 4,
      reviews: 196,
      description: "Plug-and-play AI chatbot integration.",
      technologies: ["OpenAI API", "Node.js", "Socket.io"],
      price: 999,
      image: ChatBotImage
    }
  ];

  return (
    <Wrapper>
      <Heading>Buy Digital Products</Heading>

      <Grid>
        {products.map(product => (
          <Tile key={product.id}>
            <Media>
              <img src={product.image} alt={product.name} />
            </Media>

            <ProductName>{product.name}</ProductName>

            <Stars>
              {[...Array(product.rating)].map((_, i) => <FaStar key={i} />)}
              <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                ({product.reviews})
              </span>
            </Stars>

            <TechStack>
              {product.technologies.slice(0, 3).join(" • ")}
            </TechStack>

            <Price>₹{product.price}</Price>

            <ButtonRow>
              <SecondaryButton onClick={() => setSelected(product)}>
                Details
              </SecondaryButton>
              <PrimaryButton>
                Buy Now
              </PrimaryButton>
            </ButtonRow>
          </Tile>
        ))}
      </Grid>

      <Overlay show={selected}>
        {selected && (
          <Popup>
            <Close onClick={() => setSelected(null)} />
            <h2 style={{ marginBottom: 12, color: "#fff" }}>{selected.name}</h2>

            <Description>{selected.description}</Description>

            <h4 style={{ color: "#fff" }}>Technologies Used</h4>
            <TechList>
              {selected.technologies.map((tech, i) => (
                <TechTag key={i}>{tech}</TechTag>
              ))}
            </TechList>

            <Price style={{ marginTop: 20 }}>
              ₹{selected.price}
            </Price>

            <PrimaryButton style={{ width: "100%", marginTop: 15 }}>
              Proceed to Checkout
            </PrimaryButton>
          </Popup>
        )}
      </Overlay>
    </Wrapper>
  );
};

export default Buy;